import { BOARD_INNER_OFFSET_FRAC, BOARD_INNER_SIZE_FRAC, CHECKERS_TEXTURES } from './checkersAssets.js';

// Renders the checkers board + pieces from the generated art and drives tap-to-move
// with multi-jump path disambiguation. Pure view: it never talks to the network — it
// calls back `onMove(path)` with a full engine path ([sq, sq, ...], sq = row*8+col,
// row 0 = TOP) once the player has picked an unambiguous legal move.
//
// Orientation: the engine board always has row 0 at the top with (0,0) dark. We ORIENT
// the display so MY colour sits at the BOTTOM. For 'w' (host, red) the engine already
// starts white at the bottom, so identity. For 'b' we rotate the mapping 180°
// ((r,c)->(7-r,7-c)) — mapping only; the textures are not mirrored. Spectators (null)
// see the default 'w'-at-bottom view.
export function createCheckersRenderer(scene, { x, y, size, myColor, onMove }) {
  const offset = size * BOARD_INNER_OFFSET_FRAC;
  const sq = (size * BOARD_INNER_SIZE_FRAC) / 8;
  const left = x - size / 2;
  const top = y - size / 2;

  let flip = myColor === 'b';                 // rotate mapping 180° for black
  let board = new Array(64).fill(null);
  let legalMoves = [];                        // paths for the side to move (mine only)
  let interactive = false;
  let selection = [];                         // engine squares picked so far (prefix)

  // engine (r,c) -> display (dr,dc)
  const toDisplay = (r, c) => (flip ? [7 - r, 7 - c] : [r, c]);
  // display (dr,dc) -> engine (r,c)
  const toEngine = (dr, dc) => (flip ? [7 - dr, 7 - dc] : [dr, dc]);

  // Pixel centre of an engine square.
  function squareCentre(square) {
    const r = Math.floor(square / 8), c = square % 8;
    const [dr, dc] = toDisplay(r, c);
    return {
      cx: left + offset + (dc + 0.5) * sq,
      cy: top + offset + (dr + 0.5) * sq,
    };
  }

  const container = scene.add.container(0, 0).setDepth(10);
  const boardImg = scene.add.image(x, y, CHECKERS_TEXTURES.board).setDisplaySize(size, size);
  const highlights = scene.add.graphics();               // square tints (selection / hints)
  const pieceLayer = scene.add.container(0, 0);           // piece sprites redrawn each state
  container.add([boardImg, highlights, pieceLayer]);

  // One board-wide hit zone (only the playable inner box). Converts a tap to an engine
  // square and feeds the selection state machine.
  const inner = size * BOARD_INNER_SIZE_FRAC;
  const zone = scene.add.zone(left + offset + inner / 2, top + offset + inner / 2, inner, inner)
    .setInteractive({ useHandCursor: true });
  container.add(zone);
  zone.on('pointerdown', (pointer) => {
    if (!interactive) return;
    const dc = Math.floor((pointer.worldX - (left + offset)) / sq);
    const dr = Math.floor((pointer.worldY - (top + offset)) / sq);
    if (dr < 0 || dr > 7 || dc < 0 || dc > 7) return;
    const [r, c] = toEngine(dr, dc);
    onSquareTap(r * 8 + c);
  });

  const matchesPrefix = (path, sel) => sel.every((s, i) => path[i] === s);

  function onSquareTap(square) {
    if (selection.length === 0) {
      if (legalMoves.some((p) => p[0] === square)) selection = [square];
      else return;
    } else {
      const cand = legalMoves.filter((p) => matchesPrefix(p, selection) && p[selection.length] === square);
      if (cand.length === 0) {
        // Not a valid continuation. Tapping a fresh origin restarts there; anything
        // else clears the selection.
        selection = legalMoves.some((p) => p[0] === square) ? [square] : [];
        redraw();
        return;
      }
      selection = [...selection, square];
    }

    const matching = legalMoves.filter((p) => matchesPrefix(p, selection));
    const complete = matching.filter((p) => p.length === selection.length);
    const extendable = matching.filter((p) => p.length > selection.length);
    if (extendable.length === 0 && complete.length === 1) {
      const path = complete[0];
      selection = [];
      onMove(path);
      // Leave the board as-is; the resulting match_state will refresh it.
    } else {
      redraw();
    }
  }

  function nextHops() {
    if (selection.length === 0) return [];
    const set = new Set();
    for (const p of legalMoves) {
      if (matchesPrefix(p, selection) && p.length > selection.length) set.add(p[selection.length]);
    }
    return [...set];
  }

  function redraw() {
    // Pieces
    pieceLayer.removeAll(true);
    for (let s = 0; s < 64; s++) {
      const v = board[s];
      if (!v) continue;
      const tex = CHECKERS_TEXTURES[v];
      if (!tex) continue;
      const { cx, cy } = squareCentre(s);
      pieceLayer.add(scene.add.image(cx, cy, tex).setDisplaySize(sq * 0.86, sq * 0.86));
    }

    // Highlights
    highlights.clear();
    const drawSquare = (square, color, alpha) => {
      const { cx, cy } = squareCentre(square);
      highlights.fillStyle(color, alpha);
      highlights.fillRect(cx - sq / 2, cy - sq / 2, sq, sq);
    };
    if (interactive) {
      if (selection.length === 0) {
        // Faint hint on every movable piece (also surfaces forced captures).
        const starts = new Set(legalMoves.map((p) => p[0]));
        for (const s of starts) drawSquare(s, 0x3ad07a, 0.28);
      } else {
        for (const s of selection) drawSquare(s, 0xffd166, 0.55);   // chosen path so far
        for (const s of nextHops()) drawSquare(s, 0x3ad07a, 0.5);   // where I can go next
      }
    }
  }

  function update({ board: b, legalMoves: lm, interactive: on, myColor: mc }) {
    board = b || new Array(64).fill(null);
    legalMoves = lm || [];
    interactive = !!on;
    if (mc !== undefined) flip = mc === 'b';
    selection = [];               // a new state always resets any in-progress pick
    redraw();
  }

  function clearSelection() {
    if (selection.length) { selection = []; redraw(); }
  }

  function destroy() {
    zone.destroy();
    container.destroy(true);
  }

  return {
    update, clearSelection, destroy,
    // e2e hook: drive the exact tap→disambiguation→onMove path without a real pointer.
    tapSquare: (square) => onSquareTap(square),
    getSelection: () => selection.slice(),
  };
}
